/**
 * Generic collection browser registry.
 * Add a new entry here to expose a new MongoDB collection for CRUD without writing new pages.
 */

export type FieldType = 'text' | 'select' | 'textarea'

export interface FieldOption {
  value: string
  label: string
}

export interface FieldConfig {
  key: string
  label: string
  fieldType: FieldType
  options?: FieldOption[]
  required?: boolean
  placeholder?: string
  /** Show this field only when `dependsOn.field` equals `dependsOn.value` */
  dependsOn?: { field: string; value: string }
  /** Show in the table view */
  tableVisible?: boolean
  /** Render a {{token}} code badge instead of plain text in the table */
  tableBadge?: boolean
  /** Section heading — fields with the same section are grouped together in the modal */
  section?: string
  /** Span full modal row width (defaults to half-width in 2-col grid) */
  fullWidth?: boolean
}

export interface CollectionConfig {
  id: string
  label: string
  description: string
  /** Query key prefix for React Query */
  queryKey: string
  fields: FieldConfig[]
  /** Subset of field keys shown as columns in the table (in order) */
  tableColumns: string[]
  /** Field key used as primary search/filter in the browser (must be a string field) */
  searchKey?: string
}

export const PERSONALIZED_MAPPINGS_CONFIG: CollectionConfig = {
  id: 'personalized-mappings',
  label: 'Personalized Tokens',
  description: 'Manage personalization tokens used in campaign message templates.',
  queryKey: 'personalized-mappings',
  searchKey: 'tokenName',
  tableColumns: ['tokenName', 'displayName', 'sourceType', 'entity', 'category', 'type', 'dataType', 'status'],
  fields: [
    // ── Identity ──────────────────────────────────────────────────────────────
    {
      key: 'tokenName',
      label: 'Token Name',
      fieldType: 'text',
      required: true,
      placeholder: 'e.g. firstName',
      section: 'Identity',
      tableVisible: true,
      tableBadge: true,
    },
    {
      key: 'displayName',
      label: 'Display Name',
      fieldType: 'text',
      required: true,
      placeholder: 'e.g. First Name',
      section: 'Identity',
      tableVisible: true,
    },
    // ── Source ────────────────────────────────────────────────────────────────
    {
      key: 'sourceType',
      label: 'Source Type',
      fieldType: 'select',
      required: true,
      options: [
        { value: 'customer', label: 'Customer' },
        { value: 'event', label: 'Event' },
        { value: 'user', label: 'User' },
        { value: 'segment', label: 'Segment' },
      ],
      section: 'Source',
      tableVisible: true,
    },
    {
      key: 'entity',
      label: 'Entity',
      fieldType: 'text',
      required: true,
      placeholder: 'e.g. customer, events',
      section: 'Source',
      tableVisible: true,
    },
    {
      key: 'category',
      label: 'Category',
      fieldType: 'text',
      required: true,
      placeholder: 'e.g. profile, transaction',
      section: 'Source',
      tableVisible: true,
    },
    {
      key: 'eventName',
      label: 'Event Name',
      fieldType: 'text',
      placeholder: 'e.g. add_to_cart',
      section: 'Source',
      dependsOn: { field: 'sourceType', value: 'event' },
    },
    // ── Mapping ───────────────────────────────────────────────────────────────
    {
      key: 'type',
      label: 'Token Type',
      fieldType: 'select',
      required: true,
      options: [
        { value: 'system', label: 'System' },
        { value: 'custom', label: 'Custom' },
        { value: 'user', label: 'User' },
      ],
      section: 'Mapping',
      tableVisible: true,
    },
    {
      key: 'attributeName',
      label: 'Attribute Name',
      fieldType: 'text',
      required: true,
      placeholder: 'e.g. DEMOGRAPHY_FIRST_NAME',
      section: 'Mapping',
      fullWidth: true,
    },
    {
      key: 'dataType',
      label: 'Data Type',
      fieldType: 'select',
      required: true,
      options: [
        { value: 'string', label: 'String' },
        { value: 'number', label: 'Number' },
        { value: 'boolean', label: 'Boolean' },
        { value: 'date', label: 'Date' },
        { value: 'array', label: 'Array' },
      ],
      section: 'Mapping',
      tableVisible: true,
    },
    {
      key: 'aggregation',
      label: 'Aggregation',
      fieldType: 'select',
      required: true,
      options: [
        { value: 'latest', label: 'Latest' },
        { value: 'first', label: 'First' },
        { value: 'sum', label: 'Sum' },
        { value: 'avg', label: 'Average' },
        { value: 'count', label: 'Count' },
      ],
      section: 'Mapping',
    },
    {
      key: 'timeWindow',
      label: 'Time Window',
      fieldType: 'text',
      placeholder: 'e.g. 30d, 7d, 24h',
      section: 'Mapping',
      dependsOn: { field: 'sourceType', value: 'event' },
    },
    // ── Settings ──────────────────────────────────────────────────────────────
    {
      key: 'fallbackValue',
      label: 'Fallback Value',
      fieldType: 'text',
      placeholder: 'e.g. Valued Customer',
      section: 'Settings',
    },
    {
      key: 'status',
      label: 'Status',
      fieldType: 'select',
      required: true,
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
      section: 'Settings',
      tableVisible: true,
    },
  ],
}

/** Registry of all collections available under Product Data. */
export const COLLECTION_REGISTRY: CollectionConfig[] = [
  PERSONALIZED_MAPPINGS_CONFIG,
]
